"use client";

import { useQueryClient } from "@tanstack/react-query";
import { FOLLOW_STATUSES } from "@workspace/types";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { useCallback } from "react";
import { trackUserFollowed } from "@/lib/services/analytics";
import { trpc } from "@/lib/trpc/react";

/**
 * Hook for follow-related actions with optimistic updates
 * Now using tRPC mutations for cross-platform consistency (Step 1: Web-only)
 */
export function useFollowActions() {
	const utils = trpc.useUtils();
	const queryClient = useQueryClient();

	// Use tRPC mutation instead of server action
	const followUserMutation = trpc.social.followUser.useMutation({
		onMutate: async ({ targetUserId }) => {
			// Cancel any in-flight queries
			await utils.social.getFollowStatus.cancel({ targetUserId });

			// Get the current data
			const previousStatus = utils.social.getFollowStatus.getData({
				targetUserId,
			});

			// Set optimistic status based on whether target is private
			// If we know they're private, show PENDING, otherwise show ACCEPTED
			const optimisticStatus = previousStatus?.isTargetPrivate
				? FOLLOW_STATUSES.PENDING
				: FOLLOW_STATUSES.ACCEPTED;

			utils.social.getFollowStatus.setData(
				{ targetUserId },
				{
					status: optimisticStatus,
					isFollowingYou: previousStatus?.isFollowingYou || false,
					isTargetPrivate: previousStatus?.isTargetPrivate || false,
				},
			);

			return { previousStatus };
		},
		onSuccess: async (data, variables) => {
			// Use server response as source of truth - DON'T invalidate, just set the data
			utils.social.getFollowStatus.setData(
				{ targetUserId: variables.targetUserId },
				{
					status: data.status,
					isFollowingYou: false,
					isTargetPrivate:
						"isTargetPrivate" in data ? data.isTargetPrivate : false,
				},
			);

			// Track follow action
			trackUserFollowed(variables.targetUserId, "profile");

			// DON'T invalidate - it clears our cache!

			// Show appropriate message
			if (data?.message) {
				toast({ description: data.message });
			}

			// Only invalidate essential stats - content visibility is handled by real-time follow status
			queryClient.invalidateQueries({
				queryKey: ["trpc.social.getFollowStats"],
			});
			// Invalidate current user's stats too
			queryClient.invalidateQueries({
				queryKey: ["trpc.user.getUserStats"],
			});

			// Don't invalidate lists - let user stay in list with updated button state
			// Lists use DYNAMIC caching and will refresh after 5 minutes
		},
		onError: (error, { targetUserId }, context) => {
			// Revert on error
			if (context?.previousStatus) {
				utils.social.getFollowStatus.setData(
					{ targetUserId },
					context.previousStatus,
				);
			}
			// Note: We only have previousStatus in context, not previousTargetStats
			// Real-time will handle the stats updates

			// Show error toast
			const errorMessage = error.message;
			toast({
				variant: "destructive",
				description: errorMessage || "Failed to complete action",
			});
		},
	});

	const followUser = useCallback(
		(targetUserId: string) => {
			return followUserMutation.mutateAsync({ targetUserId });
		},
		[followUserMutation],
	);

	// Use tRPC mutation instead of server action for unfollowing
	const unfollowUserMutation = trpc.social.unfollowUser.useMutation({
		onMutate: async ({ targetUserId }) => {
			// Cancel any in-flight queries
			await utils.social.getFollowStatus.cancel({ targetUserId });

			const previousStatus = utils.social.getFollowStatus.getData({
				targetUserId,
			});

			// Instant optimistic update - always show "Follow"
			utils.social.getFollowStatus.setData(
				{ targetUserId },
				{
					status: FOLLOW_STATUSES.NOT_FOLLOWING,
					isFollowingYou: previousStatus?.isFollowingYou || false,
					isTargetPrivate: previousStatus?.isTargetPrivate || false,
				},
			);

			return { previousStatus };
		},
		onSuccess: async (data, variables) => {
			// Set to NOT_FOLLOWING
			utils.social.getFollowStatus.setData(
				{ targetUserId: variables.targetUserId },
				{
					status: FOLLOW_STATUSES.NOT_FOLLOWING,
					isFollowingYou: false,
					isTargetPrivate:
						"isTargetPrivate" in data ? data.isTargetPrivate : false,
				},
			);

			// Force invalidate to trigger re-render of all components watching this query
			await utils.social.getFollowStatus.invalidate({
				targetUserId: variables.targetUserId,
			});


			if (data?.message) {
				toast({ description: data.message });
			}

			// Only invalidate essential stats - content visibility is handled by real-time follow status
			queryClient.invalidateQueries({
				queryKey: ["trpc.social.getFollowStats"],
			});
			// Invalidate current user's stats too
			queryClient.invalidateQueries({
				queryKey: ["trpc.user.getUserStats"],
			});

			// Don't invalidate lists - let user stay in list with updated button state
			// Lists use DYNAMIC caching and will refresh after 5 minutes
		},
		onError: (error, { targetUserId }, context) => {
			// Revert on error
			if (context?.previousStatus) {
				utils.social.getFollowStatus.setData(
					{ targetUserId },
					context.previousStatus,
				);
			}

			// Show error toast
			const errorMessage = error.message;
			toast({
				variant: "destructive",
				description: errorMessage || "Failed to unfollow user",
			});
		},
	});

	const unfollowUser = useCallback(
		(targetUserId: string) => {
			return unfollowUserMutation.mutateAsync({ targetUserId });
		},
		[unfollowUserMutation],
	);

	// Accept/reject follow requests - using tRPC mutations
	const acceptFollowRequestMutation =
		trpc.social.acceptFollowRequest.useMutation({
			onMutate: async ({ followerId }) => {
				// Cancel any outgoing refetches
				await utils.social.getNotifications.cancel();

				// Optimistically remove the follow request notification
				const previousData = utils.social.getNotifications.getData({
					limit: 5,
					page: 1,
					includeDismissed: false,
				});

				utils.social.getNotifications.setData(
					{ limit: 5, page: 1, includeDismissed: false },
					(old) => {
						if (!old) return old;
						return {
							...old,
							notifications: old.notifications.filter(
								(n) =>
									!(
										n.type === "follow_request" &&
										n.content.followerId === followerId
									),
							),
						};
					},
				);

				return { previousData };
			},
			onSuccess: (data, variables) => {
				// Invalidate our own notifications
				utils.social.getNotifications.invalidate();
				utils.social.getNotificationCount.invalidate();
				utils.social.getFollowStats.invalidate();

				// Note: We can't invalidate the requester's cache from here
				// since each user has their own browser/cache
				// User A will pick up the status change via polling (15s interval)

				toast({ description: "Follow request accepted" });
			},
			onError: (error, variables, context) => {
				// Revert on error
				if (context?.previousData) {
					utils.social.getNotifications.setData(
						{ limit: 5, page: 1, includeDismissed: false },
						context.previousData,
					);
				}

				const errorMessage = error.message;
				toast({
					variant: "destructive",
					description: errorMessage || "Failed to accept request",
				});
			},
		});

	const rejectFollowRequestMutation =
		trpc.social.rejectFollowRequest.useMutation({
			onMutate: async ({ followerId }) => {
				// Cancel any outgoing refetches
				await utils.social.getNotifications.cancel();

				// Optimistically remove the follow request notification
				const previousData = utils.social.getNotifications.getData({
					limit: 5,
					page: 1,
					includeDismissed: false,
				});

				utils.social.getNotifications.setData(
					{ limit: 5, page: 1, includeDismissed: false },
					(old) => {
						if (!old) return old;
						return {
							...old,
							notifications: old.notifications.filter(
								(n) =>
									!(
										n.type === "follow_request" &&
										n.content.followerId === followerId
									),
							),
						};
					},
				);

				return { previousData };
			},
			onSuccess: () => {
				// Only invalidate OUR OWN notifications - don't try to update other users
				utils.social.getNotifications.invalidate();
				utils.social.getNotificationCount.invalidate();
				utils.social.getFollowStats.invalidate();

				toast({ description: "Follow request rejected" });
			},
			onError: (error, variables, context) => {
				// Revert on error
				if (context?.previousData) {
					utils.social.getNotifications.setData(
						{ limit: 5, page: 1, includeDismissed: false },
						context.previousData,
					);
				}

				const errorMessage = error.message;
				toast({
					variant: "destructive",
					description: errorMessage || "Failed to reject request",
				});
			},
		});

	const acceptFollowRequest = useCallback(
		(followerId: string) => {
			return acceptFollowRequestMutation.mutateAsync({ followerId });
		},
		[acceptFollowRequestMutation],
	);

	const rejectFollowRequest = useCallback(
		(followerId: string) => {
			return rejectFollowRequestMutation.mutateAsync({ followerId });
		},
		[rejectFollowRequestMutation],
	);

	return {
		followUser,
		unfollowUser,
		acceptFollowRequest,
		rejectFollowRequest,
		isPending:
			followUserMutation.isPending ||
			unfollowUserMutation.isPending ||
			acceptFollowRequestMutation.isPending ||
			rejectFollowRequestMutation.isPending,
	};
}
